import { writable, get } from 'svelte/store';

// ═══ РАСКЛАДКА ХУДА (редактируется админом 9 уровня, общая для всех игроков) ═══
//
// Позиция каждого блока хранится в ПРОЦЕНТАХ экрана + якорь. Благодаря этому
// блок стоит в одном и том же месте на любом разрешении: 2% от ширины — это 2%
// и на 1280, и на 3840. Внутренние размеры блоков заданы в vh, поэтому они тоже
// масштабируются вместе с экраном; `s` — дополнительный ручной масштаб.
//
// anchor — какая точка блока ставится в (x, y):
//   tl tc tr
//   lc cc rc
//   bl bc br

export const HUD_PARTS = [
    { id: 'brand',    name: 'Бренд, время, ID' },
    { id: 'money',    name: 'Деньги (наличные и банк)' },
    { id: 'stats',    name: 'Статусы (жизни, броня, еда, вода)' },
    { id: 'street',   name: 'Адрес (улица)' },
    { id: 'speedo',   name: 'Спидометр' },
    { id: 'hints',    name: 'Подсказки клавиш' },
    { id: 'quest',    name: 'Задание (квест-худ)' },
    { id: 'notify',   name: 'Уведомления и объявления' },
    { id: 'level',    name: 'Поднятие уровня' },
    { id: 'gangwar',  name: 'Забив за бизнес' },
];

// Стандартная раскладка = то, где блоки стоят сейчас.
export const DEFAULT_LAYOUT = {
    brand:    { x: 98.0, y: 2.2,  s: 1, anchor: 'tr', pin: 'screen', hidden: false },
    money:    { x: 98.0, y: 7.4,  s: 1, anchor: 'tr', pin: 'screen', hidden: false },
    stats:    { x: 1.5,  y: 97.5, s: 1, anchor: 'bl', pin: 'screen', hidden: false },
    street:   { x: 99.0, y: 97.5, s: 1, anchor: 'br', pin: 'screen', hidden: false },
    speedo:   { x: 99.0, y: 98.5, s: 1, anchor: 'br', pin: 'screen', hidden: false },
    hints:    { x: 99.0, y: 50.0, s: 1, anchor: 'rc', pin: 'screen', hidden: false },
    quest:    { x: 98.0, y: 13.5, s: 1, anchor: 'tr', pin: 'screen', hidden: false },
    notify:   { x: 50.0, y: 2.0,  s: 1, anchor: 'tc', pin: 'screen', hidden: false },
    level:    { x: 50.0, y: 88.0, s: 1, anchor: 'bc', pin: 'screen', hidden: false },
    gangwar:  { x: 1.5,  y: 26.0, s: 1, anchor: 'tl', pin: 'screen', hidden: false },
};

const clone = (o) => JSON.parse(JSON.stringify(o));

const normalize = (raw) => {
    const out = clone(DEFAULT_LAYOUT);
    if (!raw || typeof raw !== 'object') return out;
    for (const id of Object.keys(out)) {
        const p = raw[id];
        if (!p || typeof p !== 'object') continue;
        if (typeof p.x === 'number' && isFinite(p.x)) out[id].x = Math.max(-20, Math.min(120, p.x));
        if (typeof p.y === 'number' && isFinite(p.y)) out[id].y = Math.max(-20, Math.min(120, p.y));
        if (typeof p.s === 'number' && isFinite(p.s)) out[id].s = clampScale(p.s);
        if (typeof p.anchor === 'string' && ANCHOR_OFFSET[p.anchor]) out[id].anchor = p.anchor;
        out[id].pin = p.pin === 'map' ? 'map' : 'screen';
        out[id].hidden = !!p.hidden;
    }
    return out;
};

export const ANCHOR_OFFSET = {
    tl: [0, 0],     tc: [-50, 0],     tr: [-100, 0],
    lc: [0, -50],   cc: [-50, -50],   rc: [-100, -50],
    bl: [0, -100],  bc: [-50, -100],  br: [-100, -100],
};

export const ANCHOR_NAME = {
    tl: 'левый верх',   tc: 'центр сверху',  tr: 'правый верх',
    lc: 'левый центр',  cc: 'центр',         rc: 'правый центр',
    bl: 'левый низ',    bc: 'центр снизу',   br: 'правый низ',
};

// Точка, ОТ которой блок растёт при увеличении: та же, за которую он привязан.
// Иначе увеличенный блок «уползает» из угла, куда его поставили.
export const ANCHOR_ORIGIN = {
    tl: 'left top',     tc: 'center top',     tr: 'right top',
    lc: 'left center',  cc: 'center center',  rc: 'right center',
    bl: 'left bottom',  bc: 'center bottom',  br: 'right bottom',
};

export const SCALE_MIN = 0.4;
export const SCALE_MAX = 3;
export const clampScale = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return 1;
    return Math.round(Math.max(SCALE_MIN, Math.min(SCALE_MAX, n)) * 100) / 100;
};

// Где сейчас на экране миникарта (пиксели). Меряет сам худ при каждом
// изменении её размера (обычная / большая / скрытая) — блоки с привязкой
// «к карте» переезжают вместе с ней.
export const mapRect = writable({ x: 0, y: 0, w: 0, h: 0, ok: false });

export const hudLayout = writable(clone(DEFAULT_LAYOUT));
export const hudEditMode = writable(false);

// Что сейчас показывают направляющие редактора: выделенный или перетаскиваемый
// блок, его рамка на экране, совпадения с другими блоками и расстояния.
// null — направляющих нет. Заполняет hudbox, рисует hudguides.
export const hudGuides = writable(null);
export const hudEditSelected = writable('brand');

// css для блока: якорная точка блока попадает ровно в (x%, y%) экрана,
// а у блоков с привязкой к карте — в (x%, y%) от РАЗМЕРА МИНИКАРТЫ, считая
// от её левого верхнего угла. Поэтому такой блок сам едет за картой, когда
// она меняет размер (большая карта / обычная) или уезжает по безопасной зоне.
export const partStyle = (part, rect) => {
    if (!part) return '';
    const [ax, ay] = ANCHOR_OFFSET[part.anchor] || ANCHOR_OFFSET.tl;
    const tr = `transform:translate(${ax}%,${ay}%) scale(${part.s});`
        + `transform-origin:${ANCHOR_ORIGIN[part.anchor] || 'left top'};`;

    if (part.pin === 'map' && rect && rect.ok && rect.w > 0 && rect.h > 0) {
        const px = rect.x + part.x / 100 * rect.w;
        const py = rect.y + part.y / 100 * rect.h;
        return `left:${px.toFixed(1)}px;top:${py.toFixed(1)}px;` + tr;
    }
    return `left:${part.x}%;top:${part.y}%;` + tr;
};

window.hudLayoutStore = {
    // раскладка с сервера (одна на всех игроков)
    set: (json) => {
        try {
            // Во время правки раскладку с сервера НЕ принимаем: клиент повторяет
            // её несколько раз после каждой рассылки, и блок, который админ
            // только что перетащил, прыгал обратно на старое место.
            if (get(hudEditMode)) return;
            hudLayout.set(normalize(typeof json === 'string' ? JSON.parse(json) : json));
        } catch (e) { /* битый json — остаёмся на текущей раскладке */ }
    },
    // режим редактирования (команда админа 9 уровня)
    edit: (on) => hudEditMode.set(!!on),
    get: () => JSON.stringify(get(hudLayout)),
};

export const applyPatch = (id, patch) => {
    hudLayout.update((l) => {
        if (!l[id]) return l;
        l[id] = { ...l[id], ...patch };
        return l;
    });
};

// изменить масштаб блока на шаг (колесо мыши, кнопки −/+ в редакторе)
export const scaleBy = (id, step) => {
    const l = get(hudLayout);
    if (!l[id]) return;
    applyPatch(id, { s: clampScale((l[id].s || 1) + step) });
};

export const resetLayout = () => hudLayout.set(clone(DEFAULT_LAYOUT));
